import { supabase } from '@/lib/supabase'
import type {
  PartyDistrictState,
  PartyDistrictStateWithDistrict,
  PartyDistrictStateWithParty,
  PartyDistrictStateWithAll,
  UpdatePartyDistrictStateInput,
  BulkPartyDistrictStateUpdate,
} from '@/types'

// Query key factory for consistent cache management
export const partyDistrictStateKeys = {
  all: ['partyDistrictStates'] as const,
  byParty: (partyId: string) => ['partyDistrictStates', 'party', partyId] as const,
  byPartyWithDistricts: (partyId: string) =>
    ['partyDistrictStates', 'party', partyId, 'withDistricts'] as const,
  byDistrict: (districtId: string) => ['partyDistrictStates', 'district', districtId] as const,
  byDistrictWithParties: (districtId: string) =>
    ['partyDistrictStates', 'district', districtId, 'withParties'] as const,
  byNation: (nationId: string) => ['partyDistrictStates', 'nation', nationId] as const,
  single: (partyId: string, districtId: string) =>
    ['partyDistrictStates', 'single', partyId, districtId] as const,
}

// Fetch all district states for a party
export async function fetchPartyDistrictStates(partyId: string): Promise<PartyDistrictState[]> {
  const { data, error } = await supabase
    .from('party_district_states')
    .select('*')
    .eq('party_id', partyId)
    .order('district_id', { ascending: true })

  if (error) {
    throw error
  }

  return data as PartyDistrictState[]
}

// Fetch all district states for a party with district details
export async function fetchPartyDistrictStatesWithDistricts(
  partyId: string
): Promise<PartyDistrictStateWithDistrict[]> {
  const { data, error } = await supabase
    .from('party_district_states')
    .select(
      `
      *,
      district:districts(
        id,
        name,
        settlement_type,
        population,
        region:regions(id, name)
      )
    `
    )
    .eq('party_id', partyId)
    .order('district_id', { ascending: true })

  if (error) {
    throw error
  }

  return data as PartyDistrictStateWithDistrict[]
}

// Fetch state for specific party+district
export async function fetchPartyDistrictState(
  partyId: string,
  districtId: string
): Promise<PartyDistrictState> {
  const { data, error } = await supabase
    .from('party_district_states')
    .select('*')
    .eq('party_id', partyId)
    .eq('district_id', districtId)
    .single()

  if (error) {
    throw error
  }

  return data as PartyDistrictState
}

// Fetch all parties' states for a district (for comparison)
export async function fetchDistrictPartyStates(districtId: string): Promise<PartyDistrictState[]> {
  const { data, error } = await supabase
    .from('party_district_states')
    .select('*')
    .eq('district_id', districtId)
    .order('recognition', { ascending: false })

  if (error) {
    throw error
  }

  return data as PartyDistrictState[]
}

// Update state (game mechanics only - not direct user action)
export async function updatePartyDistrictState(
  partyId: string,
  districtId: string,
  updates: UpdatePartyDistrictStateInput
): Promise<PartyDistrictState> {
  const { data, error } = await supabase
    .from('party_district_states')
    .update(updates)
    .eq('party_id', partyId)
    .eq('district_id', districtId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as PartyDistrictState
}

// Bulk update (for tick processing)
export async function bulkUpdatePartyDistrictStates(
  updates: BulkPartyDistrictStateUpdate[]
): Promise<void> {
  // Supabase doesn't support bulk upserts with different values per row easily,
  // so we need to do individual updates. For better performance in production,
  // consider using an RPC function or transaction.
  const promises = updates.map(({ party_id, district_id, ...data }) =>
    supabase
      .from('party_district_states')
      .update(data)
      .eq('party_id', party_id)
      .eq('district_id', district_id)
  )

  const results = await Promise.all(promises)

  // Check for any errors
  const errors = results.filter((r) => r.error)
  if (errors.length > 0) {
    throw errors[0].error
  }
}

// Fetch all parties' states for a district with party details (for competitive analysis)
export async function fetchDistrictPartyStatesWithParties(
  districtId: string
): Promise<PartyDistrictStateWithParty[]> {
  const { data, error } = await supabase
    .from('party_district_states')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color)
    `
    )
    .eq('district_id', districtId)
    .order('recognition', { ascending: false })

  if (error) {
    throw error
  }

  return data as PartyDistrictStateWithParty[]
}

// Fetch all parties' states for entire nation (aggregate view)
export async function fetchNationPartyStates(
  nationId: string
): Promise<PartyDistrictStateWithAll[]> {
  const { data, error } = await supabase
    .from('party_district_states')
    .select(
      `
      *,
      district:districts!inner(
        id,
        name,
        settlement_type,
        population,
        region:regions!inner(id, name, nation_id)
      ),
      party:parties!inner(id, name, abbreviation, color)
    `
    )
    .eq('district.region.nation_id', nationId)

  if (error) {
    throw error
  }

  return data as PartyDistrictStateWithAll[]
}

// Initialize party district states (admin fallback if trigger fails)
export async function initializePartyDistrictStates(
  partyId: string,
  nationId: string
): Promise<void> {
  // First, get all districts for this nation
  const { data: districts, error: districtError } = await supabase
    .from('districts')
    .select('id, region:regions!inner(nation_id)')
    .eq('region.nation_id', nationId)

  if (districtError) {
    throw districtError
  }

  if (!districts || districts.length === 0) {
    return
  }

  // Create party district states for each district
  const states = districts.map((district) => ({
    party_id: partyId,
    district_id: district.id,
    recognition: 5, // Starting value from trigger
    organization_strength: 0,
  }))

  const { error: insertError } = await supabase
    .from('party_district_states')
    .upsert(states, { onConflict: 'party_id,district_id' })

  if (insertError) {
    throw insertError
  }
}
